import type { ResolvedConfig } from './types/config.js';
import type { RequestOptions } from './types/client.js';
import type { JsonRpcId, JsonRpcRequest } from '@spectrum-nodes/api-spec/models';
import type { JsonRpcResponse } from './types/index.js';
import {
  SpectrumError,
  ApiError,
  RateLimitError,
  TimeoutError,
  NetworkError,
  ChainNotFoundError,
} from './types/errors.js';
import { JSON_RPC_VERSION } from '@spectrum-nodes/api-spec/constants';
import { Cache } from './internals/cache.js';
import { withRetry } from './internals/retry.js';
import { Logger } from './internals/logger.js';
import { SDK_VERSION } from './internals/version.js';

export type { RequestOptions } from './types/client.js';

/**
 * Internal HTTP transport for the SDK. Owns the only path that talks to the
 * network. Every namespace ultimately routes through one of `jsonRpc`,
 * `rpcProxy`, or `rpcProxyBatch` here.
 *
 * Not exposed to end users — they use the `Spectrum` class. Exported for
 * SDK-internal use and for unit tests.
 */
export class HttpClient {
  readonly config: ResolvedConfig;
  readonly cache: Cache;
  readonly logger: Logger;

  /**
   * Build a client from an already-resolved config. Sets up the in-memory
   * cache and logger; does no network I/O.
   */
  constructor(config: ResolvedConfig) {
    this.config = config;
    this.cache = new Cache(config.cache.maxEntries);
    this.logger = new Logger(config.logging.level);
  }

  /**
   * Send a raw POST with the given JSON body to the configured `baseUrl`.
   * Bypasses the cache — used by `Spectrum.jsonRpc(...)` for caller-supplied
   * raw envelopes.
   */
  async post<T>(body: unknown, options?: RequestOptions): Promise<T> {
    return this.execute<T>(this.endpointUrl(), 'POST', body, options);
  }

  /**
   * Send a single JSON-RPC call to `POST /v1`. Reads + writes the cache when
   * the resolved TTL is positive (see `getJsonRpcCacheTtl`). Throws a typed
   * `SpectrumError` subclass on failure; on success returns the inner `data`.
   */
  async jsonRpc<T>(
    method: string,
    params: Record<string, unknown> | unknown[] = {},
    options?: RequestOptions,
  ): Promise<T> {
    const request: JsonRpcRequest = {
      jsonrpc: JSON_RPC_VERSION,
      method,
      params,
      id: 1,
    };

    const cacheKey = this.buildJsonRpcCacheKey(method, params);
    const ttl = options?.cacheTtl ?? this.getJsonRpcCacheTtl(method);

    if (this.config.cache.enabled && ttl > 0) {
      const cached = this.cache.get<T>(cacheKey);
      if (cached !== undefined) {
        this.logger.debug(`Cache hit: ${method}`);
        return cached;
      }
    }

    const envelope = await this.execute<JsonRpcResponse>(
      this.endpointUrl(),
      'POST',
      request,
      options,
    );
    const result = this.unwrapJsonRpcResponse<T>(envelope, method);

    if (this.config.cache.enabled && ttl > 0) {
      this.cache.set(cacheKey, result, ttl);
    }

    return result;
  }

  /**
   * Wrap a node-level JSON-RPC call (e.g. `eth_call`) inside Spectrum's
   * `rpcProxy` server method. Forces `cacheTtl: 0` — node RPC results are
   * never cached because chain head moves.
   */
  async rpcProxy(
    chain: string,
    request: { method: string; params?: unknown[]; id?: JsonRpcId },
    options?: RequestOptions,
  ): Promise<unknown> {
    return this.jsonRpc(
      'rpcProxy',
      {
        chain,
        method: request.method,
        params: request.params ?? [],
      },
      { ...options, cacheTtl: 0 },
    );
  }

  /**
   * Send a batch of node-level RPC calls as a single outer JSON-RPC batch
   * where each entry is an `rpcProxy` envelope. The inner array is returned
   * with each entry already unwrapped (envelope errors throw `ApiError`).
   * Cache forced off; ids default to `index + 1` when not supplied.
   */
  async rpcProxyBatch(
    chain: string,
    requests: Array<{ method: string; params?: unknown[]; id?: JsonRpcId }>,
    options?: RequestOptions,
  ): Promise<unknown[]> {
    const outerBatch: JsonRpcRequest[] = requests.map((request, index) => ({
      jsonrpc: JSON_RPC_VERSION,
      method: 'rpcProxy',
      params: {
        chain,
        method: request.method,
        params: request.params ?? [],
      },
      id: request.id ?? index + 1,
    }));

    const envelopes = await this.execute<JsonRpcResponse[]>(
      this.endpointUrl(),
      'POST',
      outerBatch,
      {
        ...options,
        cacheTtl: 0,
      },
    );

    if (!Array.isArray(envelopes)) {
      throw new ApiError('Expected JSON-RPC batch response', 500, this.endpointUrl());
    }

    return envelopes.map((envelope) => this.unwrapJsonRpcResponse(envelope, 'rpcProxy'));
  }

  /** Drop every cached response. Doesn't affect inflight requests. */
  clearCache(): void {
    this.cache.clear();
  }

  /**
   * Validate a JSON-RPC 2.0 envelope and return its inner result.
   *
   * - Missing/non-object payloads → `ApiError` (invalid response)
   * - Envelope-level `error` field → `ApiError` (server-reported failure)
   * - Envelope `result` present → unwraps the `data` envelope if shaped that way
   * - Envelope with neither `result` nor `error` → `ApiError` (malformed)
   */
  private unwrapJsonRpcResponse<T>(payload: unknown, method: string): T {
    const url = this.endpointUrl();
    if (!payload || typeof payload !== 'object' || !('jsonrpc' in payload)) {
      throw new ApiError(`Invalid JSON-RPC response for ${method}`, 500, url, payload);
    }

    const envelope = payload as JsonRpcResponse;
    if ('error' in envelope && envelope.error) {
      throw new ApiError(envelope.error.message, 500, url, envelope.error);
    }

    if ('result' in envelope) {
      return this.extractDataEnvelope(envelope.result) as T;
    }

    throw new ApiError(`Invalid JSON-RPC response for ${method}`, 500, url, payload);
  }

  /**
   * Strip Spectrum's `{ data: ... }` wrapper if present. Some responses embed
   * the payload one level deep; others (raw RPC pass-through) don't.
   */
  private extractDataEnvelope(result: unknown): unknown {
    if (result && typeof result === 'object' && 'data' in result) {
      return (result as { data: unknown }).data;
    }
    return result;
  }

  /**
   * Compose a deterministic cache key from a JSON-RPC `method` + `params`.
   * Uses `stableStringify` so logically-equivalent params (e.g. `{a:1,b:2}`
   * vs `{b:2,a:1}`) produce the same key.
   */
  private buildJsonRpcCacheKey(
    method: string,
    params: Record<string, unknown> | unknown[],
  ): string {
    return `${method}:${this.stableStringify(params)}`;
  }

  /**
   * Stable JSON serializer for cache keys: sorts object keys alphabetically
   * and drops properties whose value is `undefined`. Arrays preserve order
   * (it matters for ABI args, RPC params, etc.). Not RFC-compliant — output
   * is meant for cache key equality only.
   */
  private stableStringify(value: unknown): string {
    if (Array.isArray(value)) {
      return `[${value.map((item) => this.stableStringify(item)).join(',')}]`;
    }
    if (value && typeof value === 'object') {
      const entries = Object.entries(value as Record<string, unknown>)
        .filter(([, entryValue]) => entryValue !== undefined)
        .sort(([left], [right]) => left.localeCompare(right));
      return `{${entries.map(([key, entryValue]) => `${JSON.stringify(key)}:${this.stableStringify(entryValue)}`).join(',')}}`;
    }
    return JSON.stringify(value);
  }

  /**
   * Core request orchestrator. Wraps `fetch` with:
   *   1. retry (`withRetry`) — driven by `isRetryable` (5xx, 408, NetworkError, etc.)
   *   2. lifecycle hooks — `onRequest` per attempt, `onResponse` on success,
   *      `onError` once at the end for non-abort SDK failures
   *   3. error mapping — non-2xx → `handleErrorResponse`; thrown DOMExceptions
   *      become `TimeoutError`; other thrown errors become `NetworkError`;
   *      caller-aborted requests rethrow the raw `DOMException` and skip the
   *      `onError` hook
   */
  private async execute<T>(
    url: string,
    method: 'POST',
    body?: unknown,
    options?: RequestOptions,
  ): Promise<T> {
    const maxRetries = options?.noRetry ? 0 : this.config.retries;

    try {
      return await withRetry(async () => {
        const attemptStart = Date.now();
        this.config.hooks.onRequest?.({ method, path: url });

        try {
          const response = await this.fetch(url, method, body, options?.signal);
          const duration = Date.now() - attemptStart;
          this.config.hooks.onResponse?.({ status: response.status, duration, path: url });

          if (!response.ok) {
            await this.handleErrorResponse(response, url);
            /* c8 ignore next -- handleErrorResponse always throws; unreachable */
          }

          const json = (await response.json()) as { data?: T };
          return (json.data !== undefined ? json.data : json) as T;
        } catch (error) {
          if (error instanceof SpectrumError) {
            throw error;
          }

          if (
            error instanceof DOMException &&
            (error.name === 'AbortError' || error.name === 'TimeoutError')
          ) {
            if (options?.signal?.aborted) {
              throw error;
            }
            throw new TimeoutError(url, this.config.timeout);
          }

          throw new NetworkError(
            url,
            /* v8 ignore next -- fetch rejections are Error instances in practice */
            error instanceof Error ? error : undefined,
          );
        }
      }, maxRetries);
    } catch (error) {
      // onError fires for every typed SDK failure except caller-initiated aborts.
      const isCallerAbort =
        error instanceof DOMException &&
        error.name === 'AbortError' &&
        options?.signal?.aborted === true;

      if (!isCallerAbort && error instanceof SpectrumError) {
        this.config.hooks.onError?.({ message: error.message, status: error.status, path: url });
      }
      throw error;
    }
  }

  /**
   * Wrap `globalThis.fetch` with a config-driven timeout and combined abort
   * signal. The caller's `signal` (if any) is merged with our internal
   * timeout signal so either can cancel the request. Cleans up event
   * listeners + the timeout in `finally`.
   */
  private async fetch(
    url: string,
    method: 'POST',
    body?: unknown,
    signal?: AbortSignal,
  ): Promise<Response> {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), this.config.timeout);

    let signalCleanup: (() => void) | undefined;
    let combinedSignal: AbortSignal;
    if (signal && typeof AbortSignal.any === 'function') {
      combinedSignal = AbortSignal.any([signal, controller.signal]);
    } else if (signal) {
      const [linked, cleanup] = this.linkSignals(signal, controller);
      combinedSignal = linked;
      signalCleanup = cleanup;
    } else {
      combinedSignal = controller.signal;
    }

    const headers: Record<string, string> = {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'User-Agent': `@spectrumnodes/sdk/${SDK_VERSION}`,
    };

    try {
      return await globalThis.fetch(url, {
        method,
        headers,
        body: JSON.stringify(body),
        signal: combinedSignal,
      });
    } finally {
      clearTimeout(timeout);
      /* v8 ignore next -- cleanup only set when AbortSignal.any is polyfilled */
      signalCleanup?.();
    }
  }

  /**
   * Polyfill for `AbortSignal.any` on runtimes that don't have it yet.
   * Forwards aborts from `external` to our internal `controller`, returning
   * the controller's signal plus a cleanup function the caller MUST run to
   * remove the listener (avoids leaks on long-lived external signals).
   */
  private linkSignals(
    external: AbortSignal,
    controller: AbortController,
  ): [AbortSignal, () => void] {
    if (external.aborted) {
      controller.abort(external.reason);
      return [controller.signal, () => {}];
    }
    const onAbort = () => controller.abort(external.reason);
    external.addEventListener('abort', onAbort, { once: true });
    return [controller.signal, () => external.removeEventListener('abort', onAbort)];
  }

  /**
   * Map a non-2xx HTTP response to the SDK's typed error hierarchy.
   *
   *   429              → `RateLimitError` (parses `Retry-After` if present)
   *   404 + chain msg  → `ChainNotFoundError` (case-insensitive,
   *                      tolerates trailing punctuation in the server text)
   *   anything else    → `ApiError` with original status + body
   *
   * Always throws — return type is `Promise<never>`.
   */
  private async handleErrorResponse(response: Response, path: string): Promise<never> {
    const text = await response.text().catch(() => '');
    let body: unknown = text;
    try {
      body = JSON.parse(text);
    } catch {
      // keep text body
    }

    const message =
      body && typeof body === 'object' && 'error' in body
        ? String((body as { error: string }).error)
        : `HTTP ${response.status}`;

    if (response.status === 429) {
      const retryAfter = response.headers.get('retry-after');
      const retryAfterMs = retryAfter !== null ? Number(retryAfter) * 1000 : undefined;
      throw new RateLimitError(path, Number.isFinite(retryAfterMs) ? retryAfterMs : undefined);
    }

    // Tolerate case + trailing punctuation drift in the server's wording
    const chainMatch =
      response.status === 404 ? message.match(/^unknown chain:?\s*(.+?)[.\s]*$/i) : null;
    if (chainMatch) {
      throw new ChainNotFoundError(chainMatch[1].trim());
    }

    throw new ApiError(message, response.status, path, body);
  }

  /** Return the configured `baseUrl` with any trailing slash stripped.
   * Also used as the `path` label populated on errors and hook payloads.
   */
  endpointUrl(): string {
    return this.config.baseUrl.replace(/\/$/, '');
  }

  /**
   * Resolve the cache TTL (ms) for a given JSON-RPC method. Order:
   *   1. user-supplied `cache.overrides` — first substring match wins
   *   2. uncacheable: `rpcProxy`, `getHealth`, `getChainHealth`, `compareGas`,
   *      `estimateGas`, `getBlock*` → 0
   *   3. `getYields*`, `getNft*`, `getCurve*`, `getMorpho*`, `getPendleV2Info`,
   *      `getDefiSummary` → 60 s (slow-moving on-chain data)
   *   4. `getTokenMetadata`, `getRegistry*`, `getProtocols`, `getProtocol`
   *      → 5 min (effectively static)
   *   5. fallback: `config.cache.ttl` (default 5 s)
   */
  private getJsonRpcCacheTtl(method: string): number {
    for (const [pattern, ttl] of Object.entries(this.config.cache.overrides)) {
      if (method.includes(pattern)) {
        return ttl;
      }
    }

    if (
      method === 'rpcProxy' ||
      method === 'getHealth' ||
      method === 'getChainHealth' ||
      method === 'compareGas' ||
      method === 'estimateGas' ||
      method.startsWith('getBlock')
    ) {
      return 0;
    }

    if (method.startsWith('getYields')) {
      return 60_000;
    }
    if (method === 'getTokenMetadata') {
      return 300_000;
    }
    if (method.startsWith('getNft')) {
      return 60_000;
    }
    if (method.startsWith('getRegistry') || method === 'getProtocols' || method === 'getProtocol') {
      return 300_000;
    }
    if (
      method.startsWith('getCurve') ||
      method.startsWith('getMorpho') ||
      method === 'getPendleV2Info' ||
      method === 'getDefiSummary'
    ) {
      return 60_000;
    }

    return this.config.cache.ttl;
  }
}
